import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityState,
  removeEntity,
  setAllEntities,
  setEntities,
  setEntity,
  updateEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { SelectedEntityState } from '../../../../shared/store/selected-entity.feature';
import { Aptitude } from '../models/aptitude.model';
import { ProcessusAptitudeService } from '../processus-aptitude.service';
import { AptitudeCreateRequest } from '../requests/create-request';
import { AptitudeUpdateRequest } from '../requests/update-request';
import { AptitudeState } from './aptitude.state';

type State = EntityState<Aptitude> & AptitudeState & SelectedEntityState;

export function avecMéthodesAptitudes<_>() {
  return signalStoreFeature(
    { state: type<State>() },
    // Updater
    withMethods((store) => ({
      chargerAptitudes: (state: { aptitudes: Aptitude[] }) => patchState(store, setAllEntities(state.aptitudes)),
      ajouterAptitude: (state: { aptitude: Aptitude }) => patchState(store, setEntity(state.aptitude)),
      ajouterAptitudes: (state: { aptitudes: Aptitude[] }) => patchState(store, setEntities(state.aptitudes)),
      modifierAptitude: (state: { idAptitude: string; aptitude: Partial<Aptitude> }) =>
        patchState(store, updateEntity({ id: state.idAptitude, changes: state.aptitude })),
      supprimerAptitude: (id: string) => patchState(store, removeEntity(id)),
      setFiltre: (filtre: string) => patchState(store, { filtre }),
    })),
    // Effects
    withMethods((store, aptitudeService = inject(ProcessusAptitudeService)) => ({
      charger: rxMethod<{
        idCartographie: string;
        nom?: string;
        idRéférentiel?: string;
        next?: (aptitudes: Aptitude[]) => void;
      }>(
        pipe(
          switchMap(({ idCartographie, nom, idRéférentiel, next }) =>
            aptitudeService.findAll(idCartographie, nom, idRéférentiel).pipe(
              tapResponse({
                next: (result) => {
                  store.chargerAptitudes({ aptitudes: result.items });
                  next?.(result.items);
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des aptitudes, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      chargerToutes: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            aptitudeService.findAll(idCartographie).pipe(
              tapResponse({
                next: (result) => store.chargerAptitudes({ aptitudes: result.items }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des aptitudes, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ data: AptitudeCreateRequest; next: () => void; error: () => void }>(
        pipe(
          switchMap(({ data, next, error }) =>
            aptitudeService.créer(data).pipe(
              tapResponse({
                next: (aptitudes) => {
                  store.ajouterAptitudes({ aptitudes });
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la création des aptitudes, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        idAptitude: string;
        data: AptitudeUpdateRequest;
        next: () => void;
        error?: () => void;
      }>(
        pipe(
          switchMap(({ idAptitude, data, next, error }) =>
            aptitudeService.modifier(idAptitude, data).pipe(
              tapResponse({
                next: () => {
                  store.modifierAptitude({
                    idAptitude,
                    aptitude: {
                      idPhase: data.idPhase!,
                      nom: data.nom,
                      indice: data.indice,
                    },
                  });
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la modification de l'aptitude, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error?.();
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ idAptitude: string; next: () => void }>(
        pipe(
          switchMap(({ idAptitude, next }) =>
            aptitudeService.supprimer(idAptitude).pipe(
              tapResponse({
                next: () => {
                  store.supprimerAptitude(idAptitude);
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
